import { useCallback, useContext, useMemo, useState } from "react"
import { useSelector } from 'react-redux'
import { SchoolContext } from "../SchoolContext"
import { useDispatch } from "react-redux";
import { change } from "redux-form";
import { toast } from "react-toastify";
import { toastConfig } from "config";
import { useQueryClient } from "@tanstack/react-query";
import { AbilityContext } from "components/Permissions/Can";
import { roleNames } from "keys/roles";
import qc from "keys/queryClient";
import Papa from 'papaparse';
import schoolsFile from 'mock/Schools.csv';

const useFormFields = () => {
    const [searchSchoolLoading, setSearchSchoolLoading] = useState(false)
    const dispatch = useDispatch()
    const queryClient = useQueryClient()
    const ability = useContext(AbilityContext)
    const isSuperUser = ability.can("manage", roleNames.super_user)

    const { isFormEditing, setIsFormEditing, isSchoolsGroup, formname, institutionCodeApproved, setInstitutionCodeApproved } = useContext(SchoolContext)

    const formValues = useSelector(state => state.form[formname]?.values)
    const { filled_fields } = formValues || {}
    const cities = queryClient.getQueryData([qc.cities])

    const searchSchool = useCallback(() => {
        setSearchSchoolLoading(true)
        fetch(schoolsFile)
            .then(response => response.text())
            .then(csvData => {
                Papa.parse(csvData, {
                    header: true,
                    complete: ({ data }) => {
                        setSearchSchoolLoading(false)
                        const school = data.find(sh => sh.institution_code == formValues?.institution_code)
                        if (school) {
                            setInstitutionCodeApproved(true)
                            dispatch(change(formname, "name", school.name))
                            dispatch(change(formname, "address", school.address))
                        }
                        else {
                            toast.error("מצטערים, לא נמצא מוסד עם קוד שהזנתם, אנא שלחו פנייה למערכת", toastConfig)
                        }
                    },
                    error: (error) => {
                        setSearchSchoolLoading(false)
                        console.error('CSV Parsing Error:', error);
                    }
                });
            })
            .catch(error => {
                console.error('Error fetching CSV file:', error);
            });
    }, [formValues?.institution_code])

    const initialFormFields = useMemo(() => [
        {
            name: "institution_code",
            component: "input",
            placeholder: "קוד מוסד",
            required: true,
        },
        {
            name: "search_institution_code",
            component: "button",
            label: "חפש",
            color: "thirdly",
            variant: "regular",
            width: 100,
            action: searchSchool,
            loading: searchSchoolLoading,
            hide: () => !isFormEditing
        }
    ], [isFormEditing, isSchoolsGroup, formValues, institutionCodeApproved, searchSchoolLoading])

    const affiliationFormFields = useMemo(() => [
        {
            name: "name",
            component: "input",
            placeholder: isSchoolsGroup ? "שם בית הספר" : "שם הלקוח",
            required: true,
            disabled: true,
            readOnly: true
        },
        {
            name: "display_name",
            component: "input",
            placeholder: "שם לתצוגה",
            disabled: !institutionCodeApproved,
            readOnly: true
        },
        {
            name: "email",
            component: "input",
            placeholder: "אמייל",
            required: true,
            disabled: !institutionCodeApproved,
            readOnly: true,
            isLocked: filled_fields?.email
        },
        {
            name: "phone",
            component: "input",
            placeholder: "טלפון",
            required: true,
            disabled: !institutionCodeApproved,
            readOnly: true,
            isLocked: filled_fields?.phone
        },
        {
            name: "logo",
            component: "uploadFile",
            placeholder: "העלאת לוגו",
            required: true,
            size: "small",
            type: "image",
            disabled: !institutionCodeApproved,
            readOnly: true,
            isLocked: filled_fields?.logo
        }
    ], [isSchoolsGroup, formValues, institutionCodeApproved, searchSchoolLoading])

    const addressFormFields = useMemo(() => [
        {
            name: "city",
            component: "autoComplete",
            placeholder: "עיר",
            options: cities,
            required: true,
            labelKey: "label",
            disabled: !institutionCodeApproved,
            readOnly: true,
            isLocked: filled_fields?.city
        },
        {
            name: "address",
            component: "input",
            placeholder: "רחוב + מספר בית",
            disabled: !institutionCodeApproved,
            readOnly: true,
            isLocked: filled_fields?.address
        }
    ], [institutionCodeApproved, cities, filled_fields])

    const divisionFormFields = useMemo(() => [{
        color: "thirdly",
        component: "checkboxList",
        name: "division",
        row: true,
        options: [{ value: "school", id: 1, label: "יסודי" }, { value: "brigade", id: 2, label: "חטיבה" }, { id: 3, value: "highSchool", label: "תיכון" }],
        disabled: !institutionCodeApproved,

    }], [institutionCodeApproved])

    const contactFormFields = useMemo(() => [
        {
            name: "contact_first_name",
            component: "input",
            placeholder: "שם האחראית, מזכירה",
            required: true,
            disabled: !institutionCodeApproved,
            isLocked: filled_fields?.contact_first_name
        },
        {
            name: "contact_last_name",
            component: "input",
            placeholder: "משפחה",
            required: true,
            disabled: !institutionCodeApproved,
            isLocked: filled_fields?.contact_last_name
        },
        {
            name: "contact_email",
            component: "input",
            placeholder: "אמייל",
            required: true,
            disabled: !institutionCodeApproved,
            isLocked: filled_fields?.contact_email
        },
        {
            name: "contact_phone",
            component: "input",
            placeholder: "טלפון",
            required: true,
            disabled: !institutionCodeApproved,
            isLocked: filled_fields?.contact_phone
        }
    ], [institutionCodeApproved, filled_fields])

    const generalFormFields = useMemo(() => [
        {
            name: "subscription_count",
            component: "inputNumber",
            placeholder: "מספר תלמידים",
            required: true,
            size: "small",
            disabled: !institutionCodeApproved || !isSuperUser
        }
    ], [institutionCodeApproved])

    return { generalFormFields, contactFormFields, divisionFormFields, initialFormFields, affiliationFormFields, addressFormFields }
}

export default useFormFields